/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useMemo } from 'react';

import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { highlightMatchedText } from '@atlassian/search-common/highlight-matched-text';

import { ProductKeys } from '../../../../../../common/constants/product-keys';
import type { SearchResultProps } from '../../../../../../common/types';
import { DescriptionBody } from '../../../../../../common/ui/description-body';
import { IconFor3P } from '../../../../../../common/ui/icon-for-3p';
import { LastModified } from '../../../../../../common/ui/last-modified';
import { SearchResult } from '../../../../../../common/ui/search-result';
import type { AdvancedSearchResultInteractedMetadata } from '../../../../../../common/utils/events/advanced-search-result-interacted';
import { enrichExcerpt, processExcerpt } from '../../../../../../common/utils/search-result-utils';
import { useFireContextMenuButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-button-clicked';
import { useFireContextMenuCopyButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-copy-button-clicked';
import { useFireCopyLinkButtonClicked } from '../../../../../../controllers/events/use-fire-copy-link-button-clicked';
import { useFireResultClicked } from '../../../../../../controllers/events/use-fire-result-clicked';
import { useFireResultCtxMenuClicked } from '../../../../../../controllers/events/use-fire-result-context-menu-clicked';
import { useFireSummarizeInChatButtonClicked } from '../../../../../../controllers/events/use-fire-summarize-in-chat-button-clicked';

import type { searchGraphCode_AtlassianSearchPage$key } from './__generated__/searchGraphCode_AtlassianSearchPage.graphql';
import { messages } from './messages';
import { truncatePath } from './truncate-path';

const styles = cssMap({
	typeLabelContainer: {
		'&::first-letter': {
			textTransform: 'capitalize',
		},
	},
});

export type SearchGraphCodeProps = SearchResultProps<searchGraphCode_AtlassianSearchPage$key>;

export const SearchGraphCode = ({
	node,
	position,
	cloudId,
	searchQuery,
	selectedProduct,
	primaryProduct,
	primarySubProduct,
	appMode,
	thirdPartyConfigs,
	mainContentWidth,
}: SearchGraphCodeProps): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment searchGraphCode_AtlassianSearchPage on SearchResultGraphCode {
				__typename
				id
				title
				url
				path
				description
				language
				lastModifiedDate
				repository {
					name
					namespace
					url
				}
			}
		`,
		node,
	);

	const { formatMessage } = useIntl();

	const metadata = useMemo(
		() =>
			({
				id: data.id,
				containerId: null,
				spaceId: null,
				contentType: 'github code',
				entityType: 'github code',
				contentId: data.id,
				product: selectedProduct ?? ProductKeys.Github,
			}) satisfies AdvancedSearchResultInteractedMetadata,
		[data.id, selectedProduct],
	);

	const onResultClicked = useFireResultClicked({ metadata, position });
	const onResultCtxMenuClicked = useFireResultCtxMenuClicked({ metadata, position });
	const onCopyLinkButtonClicked = useFireCopyLinkButtonClicked('searchResultTitle');
	const onSummarizeInChatButtonClicked = useFireSummarizeInChatButtonClicked({
		location: 'searchResult',
		metadata,
		position,
	});
	const onContextMenuCopyButtonClicked = useFireContextMenuCopyButtonClicked();
	const onContextMenuButtonClicked = useFireContextMenuButtonClicked();

	const { id, url, title, path, description, language, lastModifiedDate, repository } = data;

	const displayTitle = useMemo(() => (path ? truncatePath(path) : title) ?? '', [path, title]);
	const enrichedTitle = useMemo(() => {
		const { highlightedTitle } = highlightMatchedText(displayTitle, searchQuery.split(' '));
		return enrichExcerpt(highlightedTitle || displayTitle);
	}, [displayTitle, searchQuery]);

	const repositoryName = useMemo(() => {
		if (!repository) {
			return null;
		}
		return repository.namespace ? `${repository.namespace}/${repository.name}` : repository.name;
	}, [repository]);

	const headerMetadataParts = useMemo<Array<[string, React.ReactNode]>>(
		() =>
			(
				[
					['label', <Box xcss={styles.typeLabelContainer}>{formatMessage(messages.code)}</Box>],
					language && ['language', language],
					repositoryName && ['repository-name', repositoryName],
					lastModifiedDate && ['last-modified', <LastModified lastModified={lastModifiedDate} />],
				] as Array<[string, React.ReactNode] | false | undefined | null>
			).filter(Boolean) as Array<[string, React.ReactNode]>,
		[formatMessage, language, repositoryName, lastModifiedDate],
	);

	const enrichedDescription = useMemo(() => {
		if (!description) {
			return undefined;
		}
		const { highlightedTitle } = highlightMatchedText(description, searchQuery.split(' '));
		return processExcerpt(highlightedTitle || description);
	}, [description, searchQuery]);

	const descriptionNode = useMemo(
		() => (enrichedDescription ? <DescriptionBody description={enrichedDescription} /> : undefined),
		[enrichedDescription],
	);

	return (
		<SearchResult
			{...data}
			id={id}
			product={selectedProduct ?? ProductKeys.Github}
			productSelected={!!selectedProduct}
			primaryProduct={primaryProduct}
			primarySubProduct={primarySubProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultCtxMenuClicked}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			url={url}
			title={enrichedTitle}
			icon={
				// IconFor3P doesn't surface a code-specific icon yet; subtype is intentionally null
				// so the generic GitHub product icon is used. selectedProduct falls back to Github
				// (devinfo) so Forge GitHub variants get their dynamic connector icon.
				<IconFor3P
					product={selectedProduct ?? ProductKeys.Github}
					resourceType="code"
					subtype={null}
					thirdPartyConfigs={thirdPartyConfigs ?? {}}
				/>
			}
			headerMetadataParts={headerMetadataParts}
			description={descriptionNode}
			cloudId={cloudId}
			position={position}
			partial={data}
			appMode={appMode}
			isThirdParty
			mainContentWidth={mainContentWidth}
		/>
	);
};

// `any` matches the sibling renderer convention (see isSearchTrello, isSearchJiraIssue).
// Narrower structural types are rejected by TS for type-predicate targets against Relay key types.
export const isSearchGraphCode = (node?: any): node is searchGraphCode_AtlassianSearchPage$key =>
	node?.__typename === 'SearchResultGraphCode';
